import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { Button } from '@/components/ui/button';
import { HelpCircle, Eraser, BookOpen, Menu, Layers, Sun, Moon, Globe } from 'lucide-react';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import logoImage from '@/assets/compresor-logo.jpg';

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const backgroundRemovalEnabled = useFeatureFlag('background_removal');
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isEnglish = location.pathname.startsWith('/en');
  const langTogglePath = isEnglish ? '/' : '/en';
  const langToggleLabel = isEnglish ? 'ES' : 'EN';

  const handleNavigate = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <nav className="w-full border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img src={logoImage} alt="CompresorJPEG" className="h-10 w-auto" />
          </Link>

          <div className="hidden md:block">
            <NavigationMenu>
              <NavigationMenuList>
                {backgroundRemovalEnabled && (
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link to="/quitar-fondo">
                        <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                          <Eraser className="h-4 w-4 mr-2" />
                          Quitar Fondo
                        </Button>
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )}

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/herramientas">
                      <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                        <Layers className="h-4 w-4 mr-2" />
                        Herramientas
                      </Button>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/blog">
                      <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                        <BookOpen className="h-4 w-4 mr-2" />
                        Blog
                      </Button>

                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/faq">
                      <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                        <HelpCircle className="h-4 w-4 mr-2" />
                        FAQ
                      </Button>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="flex items-center gap-2">
            <Link to={langTogglePath}>
              <Button
                variant="ghost"
                size="sm"
                className="hidden md:inline-flex text-foreground/70 hover:text-foreground"
                aria-label={isEnglish ? 'Switch to Spanish' : 'Cambiar a inglés'}
              >
                <Globe className="h-4 w-4 mr-1.5" />
                {langToggleLabel}
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Cambiar tema"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button 
              variant="ghost" 
              className="md:hidden" 
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <img src={logoImage} alt="CompresorJPEG" className="h-8 w-auto" />
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-col py-2">
            {backgroundRemovalEnabled && (
              <button
                onClick={() => handleNavigate('/quitar-fondo')}
                className="flex items-center gap-3 px-4 py-3 text-foreground/70 hover:bg-accent hover:text-foreground transition-colors text-left"
              >
                <Eraser className="h-4 w-4" />
                Quitar Fondo
              </button>
            )}
            <button
              onClick={() => handleNavigate('/herramientas')}
              className="flex items-center gap-3 px-4 py-3 text-foreground/70 hover:bg-accent hover:text-foreground transition-colors text-left"
            >
              <Layers className="h-4 w-4" />
              Herramientas
            </button>
            <button
              onClick={() => handleNavigate('/blog')}
              className="flex items-center gap-3 px-4 py-3 text-foreground/70 hover:bg-accent hover:text-foreground transition-colors text-left"
            >
              <BookOpen className="h-4 w-4" />
              Blog
            </button>
            <button
              onClick={() => handleNavigate('/faq')}
              className="flex items-center gap-3 px-4 py-3 text-foreground/70 hover:bg-accent hover:text-foreground transition-colors text-left"
            >
              <HelpCircle className="h-4 w-4" />
              Preguntas Frecuentes
            </button>
            <div className="border-t my-2" />
            <button
              onClick={() => handleNavigate(langTogglePath)}
              className="flex items-center gap-3 px-4 py-3 text-foreground/70 hover:bg-accent hover:text-foreground transition-colors text-left"
            >
              <Globe className="h-4 w-4" />
              {isEnglish ? 'Español' : 'English'}
            </button>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-3 px-4 py-3 text-foreground/70 hover:bg-accent hover:text-foreground transition-colors text-left"
            >
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              {theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
};

export default NavBar;
